<script>
  import { getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link } from 'svelte-navigator';
  import { ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';

  import { BackgroundPreview } from '@client/pages/brandkit/components';

  import { VirtualBackgroundRoutingService } from '@client/services/virtual-background-routing';

  import { userStore } from '@client/stores/user';

  import { GROUP_TYPES } from '@client/enums';

  import { deepCopy } from '@common/utils/objects';

  export let route = {};
  export let dropIndex = null;
  export let routeIndex = null;
  export let startIndex = null;
  export let reordering = false;
  export let priority = [];
  export let updatePriorityPositions = () => {};
  export let onDelete = () => {};
  export let onRefresh = () => {};
  export let senderGroups = [];
  export let selectedRoute = null;

  const { basepath } = getContext('config');

  let routeStatus = route.status;
  let isUpdating = false;

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const drop = (e) => {
    e.dataTransfer.dropEffect = 'move';
    priority = updatePriorityPositions(priority, startIndex, dropIndex);
    dropIndex = null;
    startIndex = null;
  };

  const dragstart = (e) => {
    if (!reordering) {
      e.preventDefault();
      return false;
    }

    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.dropEffect = 'move';

    startIndex = routeIndex;
  };

  const onDeleteHandler = async () => {
    const confirmed = confirm($format('label.DELETE_ROUTE_CONFIRMATION'));

    if (confirmed) {
      isUpdating = true;

      priority = updatePriorityPositions(priority, route.priority, -1);

      const response = await VirtualBackgroundRoutingService.deleteRoute(route);

      isUpdating = false;

      if (response?.status >= 400) {
        notify($format('label.FAILURE.DELETE_ROUTE'), 'error');
        return;
      }

      notify($format('label.SUCCESS.DELETE_ROUTE'));

      onDelete(route);
    }
  };

  const onSave = async () => {
    isUpdating = true;

    const selectedGroups = (route.selectedGroups || []).map(({ value }) => value);
    const sendergroups = senderGroups.filter(({ groupname }) => selectedGroups.includes(groupname));
    const { blockname, background = {}, _id } = route;

    const payload = {
      _id,
      blockname,
      priority: route.priority,
      sendergroups,
      status: routeStatus,
      background,
      tags: []
    };

    const response = await VirtualBackgroundRoutingService.updateRoute(payload);
    const sgroups = senderGroups.filter(
      ({ groupname }) => sendergroups.filter((group) => groupname === group.groupname).length
    );

    route = {
      ...response,
      ...route,
      selectedGroups: route.selectedGroups || [],
      sendergroups: sgroups,
      status: routeStatus,
      editing: false
    };

    priority = priority.map((item) => (!item._id || item?._id === route._id ? route : item));

    onRefresh();

    isUpdating = false;
  };

  const onEditRoute = () => {
    selectedRoute = deepCopy(route);
  };

  const onDisableRoute = () => {
    routeStatus = false;
    onSave();
  };

  const onenableRoute = () => {
    routeStatus = true;
    onSave();
  };
</script>

<div
  class="route"
  class:reordering
  class:disabled={!reordering}
  class:active={reordering && dropIndex === routeIndex}
  draggable={reordering}
  role="button"
  tabindex="0"
  on:dragstart={dragstart}
  on:drop|preventDefault={drop}
  on:dragover|preventDefault
  on:dragenter|preventDefault={() => (dropIndex = routeIndex)}
>
  <Loader backdrop show={isUpdating} />

  <div class="route-section">
    {#key route.blockname}
      <div class="source">
        <BackgroundPreview
          width={1920}
          height={1080}
          background={route.brandkit.template.background}
          foreground={route.brandkit.template.foreground}
          cells={route.brandkit.template.cells}
          user={$userStore.currentUser}
        />
      </div>
    {/key}
  </div>

  <div class="route-section">
    <b>{$format('label.BACKGROUND')}</b>
    <a
      href="{basepath}/brandkit/studio/{route.blockname}"
      use:link
      style="background-color: {route.color};"
      class="blockname">{route.blockname}</a
    >
  </div>
  <div class="route-section">
    <b>{$format('label.GROUPS')}</b>
    {#if route.sendergroups.length}
      <div class="items">
        {#each route.sendergroups as group, index}
          {@const hasGroups = Object.keys(GROUP_TYPES).some((type) => group[type].length)}
          <div class="item" id="tooltip-{route.blockname}-{group.groupname}-{index}">
            <a href="{basepath}/manage/groups/{group.groupname}" use:link>{group.groupname}</a>
            {#if group.senders?.length}
              <Tooltip target="tooltip-{route.blockname}-{group.groupname}-{index}">
                {@html group.senders
                  .map(({ username }) => (username === '*' ? $format('label.ALL_USERS') : username))
                  .sort()
                  .join('<br>') || $format('label.EMPTY')}
              </Tooltip>
            {:else if hasGroups}
              {#each Object.keys(GROUP_TYPES) as type}
                {#if group[type]?.length}
                  {@const groupConfig = GROUP_TYPES[type]}
                  <Tooltip target="tooltip-{route.blockname}-{group.groupname}-{index}">
                    {@html group[type]
                      .map((item) => {
                        const name = Array.isArray(groupConfig.name)
                          ? groupConfig.name.map((value) => item[value]).join(' ')
                          : item[groupConfig.name];
                        return name + (item[groupConfig.count] ? ` (${item[groupConfig.count]})` : '');
                      })
                      .sort()
                      .join('<br>') || $format(groupConfig.genericLabel)}
                  </Tooltip>
                {/if}
              {/each}
            {:else}
              <Tooltip target="tooltip-{route.blockname}-{group.groupname}-{index}">
                {group.userAttrs?.length ? $format('label.DYNAMIC_GROUP') : $format('label.EMPTY')}
              </Tooltip>
            {/if}
          </div>
        {/each}
      </div>
    {:else}
      <div class="muted">{$format('label.NO_ASSIGNED_GROUPS')}</div>
    {/if}
  </div>

  <div class="actions">
    {#if !reordering}
      <ButtonDropdown>
        <DropdownToggle color="none" tag="span">
          <Icon name="more-vertical" class="action-icon" />
        </DropdownToggle>
        <DropdownMenu>
          <DropdownItem on:click={onEditRoute}>{$format('label.EDIT')}</DropdownItem>
          <DropdownItem divider />
          <DropdownItem on:click={onDeleteHandler}>{$format('label.DELETE')}</DropdownItem>
        </DropdownMenu>
      </ButtonDropdown>
    {/if}
  </div>
</div>

<style lang="scss" src="./route.scss"></style>
